import { useCallback } from 'react';
import { buildStandaloneHtml, htmlToExportText } from '../utils/draftUtils';
import { auditLogsAPI } from '../services/api';

/**
 * Fire-and-forget audit ping for a download action. Never blocks the export;
 * network errors are silent (the user's download still happens). The hook
 * surface passes `resourceType`, `resourceId`, and `surface` so admins can
 * later answer "who downloaded what, when, and from which screen".
 */
function pingDownload({ resourceType, resourceId, format, surface, meta }) {
  try {
    auditLogsAPI.trackDownload({ resourceType, resourceId, format, surface, meta });
  } catch (_) {
    // swallow — never block the actual download on telemetry failures
  }
}

/**
 * Reusable hook for document export actions (copy, .txt, .html, print/PDF).
 *
 * @param {React.RefObject} contentRef  — ref to the rendered content div
 * @param {Object}          options
 * @param {string}          options.title        — document title (used for the standalone HTML <title>)
 * @param {string}          [options.filename]    — pre-built, case-preserved basename (NO extension),
 *                                                  typically from buildDocBasename. Falls back to a
 *                                                  slug of `title` when omitted.
 * @param {string}          options.fallbackHtml  — HTML to use when contentRef is empty
 * @param {Function}        [options.onNotify]    — optional (message, type) callback for toast/alert
 * @param {Object}          [options.audit]       — { resourceType, resourceId, surface, meta }
 *                                                  for download tracking (sent fire-and-forget).
 * @returns {{ copy, downloadTxt, downloadHtml, print, getHtml }}
 */
export function useDocumentExport(contentRef, { title = 'Document', filename, fallbackHtml = '', onNotify, audit } = {}) {
  const notify = useCallback((msg, type) => {
    if (onNotify) onNotify(msg, type);
  }, [onNotify]);

  const getHtml = useCallback(() => {
    return contentRef.current ? contentRef.current.innerHTML : fallbackHtml;
  }, [contentRef, fallbackHtml]);

  // A caller-supplied basename is already slug-safe (buildDocBasename) and keeps
  // its casing; only the legacy title fallback gets the lowercase-slug treatment.
  const safeFilename = useCallback((ext) => {
    const base = filename
      ? filename
      : (title || 'document').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    return `${base || 'document'}.${ext}`;
  }, [filename, title]);

  const copy = useCallback(async () => {
    const html = getHtml();
    if (!html) return;
    try {
      await navigator.clipboard.writeText(htmlToExportText(html));
      notify('Copied to clipboard', 'success');
      if (audit) pingDownload({ ...audit, format: 'clipboard-text' });
    } catch {
      notify('Copy failed', 'error');
    }
  }, [getHtml, notify, audit]);

  const downloadTxt = useCallback(() => {
    const html = getHtml();
    if (!html) return;
    const blob = new Blob([htmlToExportText(html)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename('txt');
    a.click();
    URL.revokeObjectURL(url);
    notify('Downloaded as text', 'success');
    if (audit) pingDownload({ ...audit, format: 'txt' });
  }, [getHtml, safeFilename, notify, audit]);

  const downloadHtml = useCallback(() => {
    const html = getHtml();
    if (!html) return;
    const blob = new Blob([buildStandaloneHtml(title, html)], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename('html');
    a.click();
    URL.revokeObjectURL(url);
    notify('HTML exported', 'success');
    if (audit) pingDownload({ ...audit, format: 'html' });
  }, [getHtml, title, safeFilename, notify, audit]);

  const print = useCallback(() => {
    const html = getHtml();
    if (!html) return;
    // Print via a hidden iframe rather than window.open. A popup opened AFTER an
    // await (the numbered-PDF/DOCX fallback path calls print() inside a catch)
    // loses the user-gesture, so the browser blocks it even when pop-ups are
    // allowed. An iframe prints in-page with no popup and no gesture requirement.
    const doc = buildStandaloneHtml(title, html);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    const cleanup = () => { if (document.body.contains(iframe)) iframe.remove(); };
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        win.focus();
        // Remove after the dialog closes; hard-stop cleanup in case it never fires.
        win.onafterprint = () => setTimeout(cleanup, 300);
        win.print();
        setTimeout(cleanup, 60000);
      } catch (e) {
        notify('Unable to open the print dialog.', 'warning');
        cleanup();
      }
    };
    // srcdoc gives a reliable load event (waits for the letterhead image etc.),
    // unlike document.write into a bare iframe.
    iframe.srcdoc = doc;
    document.body.appendChild(iframe);
    if (audit) pingDownload({ ...audit, format: 'print-pdf' });
  }, [getHtml, title, notify, audit]);

  return { copy, downloadTxt, downloadHtml, print, getHtml };
}
